import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, KeyRound, Recycle, SlidersHorizontal, RotateCcw, Trash2, DatabaseBackup, FileDown } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import Field from "@/components/Field";
import Modal from "@/components/Modal";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCloud } from "@/context/CloudContext";

export default function SettingsPage() {
  const { user, readOnly } = useAuth();
  const { state: cloudState } = useCloud();
  const isAdmin = user?.role === "admin";
  const [pwUser, setPwUser] = useState("BDXGroup");
  const [pw, setPw] = useState("");
  const [recycled, setRecycled] = useState([]);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = () => {
    api.get("/recycle-bin").then((r) => setRecycled(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connected = cloudState?.status === "Connected";

  const downloadExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/system/complete-backup", { timeout: 3600000 });
      const d = r.data;
      if (d?.status !== "COMPLETE" || !d?.download_url) throw new Error("Backup did not complete");
      const href = d.download_mode === "signed_url"
        ? d.download_url
        : `${process.env.REACT_APP_BACKEND_URL}${d.download_url}`;
      const a = document.createElement("a");
      a.href = href;
      a.download = d.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Backup complete & verified — downloading ${d.filename} (link valid ${d.expires_minutes} min)`);
    } catch (err) {
      if (err?.response?.status === 409) toast.error("A Complete Backup is already being generated — please wait");
      else toast.error(err?.response?.data?.detail || apiError(err));
    } finally { setExporting(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { username: pwUser, new_password: pw });
      toast.success(`Password updated for ${pwUser}`);
      setPw("");
    } catch (err) { toast.error(apiError(err)); }
  };

  const restore = async (p) => {
    try {
      await api.post(`/projects/${p.id}/restore`);
      toast.success(`Project ${p.project_number} restored`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const permanentDelete = async () => {
    try {
      await api.delete(`/projects/${confirmTarget.id}/permanent?confirm=${encodeURIComponent(confirmText)}`);
      toast.success("Project permanently deleted");
      setConfirmTarget(null);
      setConfirmText("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="settings-page" className="bdx-page-enter">
      <Breadcrumbs items={[{ label: "Dashboard", to: "/" }, { label: "Settings" }]} />
      <div className="border-b border-gray-300 pb-7">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.15em" }}><SlidersHorizontal size={15} strokeWidth={1.9} /> BDX Administration</div>
        <h1 className="font-heading text-3xl font-bold text-gray-950 sm:text-4xl">Settings</h1>
        <p className="mt-2 text-sm text-gray-500">Application configuration and administration</p>
      </div>

      <div className="mt-6 grid max-w-[1300px] grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="bdx-icon-tile"><Cloud size={16} strokeWidth={1.9} /></div>
            <h2 className="font-heading text-base font-bold text-gray-950">Cloud Platform</h2>
            <span data-testid="cloud-status" className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase ${connected ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} style={{ letterSpacing: "0.07em" }}>
              {connected ? "Cloud Connected" : "Reconnecting"}
            </span>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            The BDX Web Edition runs on managed cloud infrastructure. Business data is held in a persistent managed
            database and all project documents, quotations, OHS files and Site Photos are stored in durable cloud
            object storage. Data persists across application restarts and deployments.
          </p>
          <div className="rounded-lg border border-gray-200 bg-[#faf9f8] px-4 py-3">
            <div className="bdx-label">Access State</div>
            <div data-testid="access-state" className={`mt-1 text-sm font-semibold ${isAdmin ? "text-emerald-700" : "text-amber-700"}`}>
              {isAdmin ? "Read / Write" : "Read Only — User Permission"}
            </div>
          </div>
          {isAdmin && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3.5">
              <div className="flex items-center gap-2">
                <DatabaseBackup size={15} strokeWidth={1.8} className="text-[#604184]" />
                <div className="font-heading text-sm font-bold text-gray-950">Complete Disaster Recovery Backup</div>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
                Download a complete point-in-time recovery package containing the current BDX Recovery application,
                all project records, documents and Site Photos for fully offline emergency access. The package runs
                on a separate Windows PC with no internet or cloud access and becomes an independent local system —
                it never synchronises changes back to production.
              </p>
              {exporting && (
                <div data-testid="backup-progress" className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                  Preparing Complete Backup — exporting the database, gathering every stored file, verifying checksums
                  and staging the package securely. Your download starts automatically once verification completes.
                </div>
              )}
              <button data-testid="complete-backup-btn" className="bdx-btn-primary mt-3 inline-flex items-center gap-1.5 text-xs" disabled={exporting} onClick={downloadExport}>
                <FileDown size={13} strokeWidth={1.75} /> {exporting ? "Generating & Verifying…" : "Download Complete Backup"}
              </button>
            </div>
          )}
        </section>

        {!readOnly && (
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="bdx-icon-tile"><KeyRound size={16} strokeWidth={1.9} /></div>
              <h2 className="font-heading text-base font-bold text-gray-950">Account Passwords</h2>
            </div>
            <p className="text-xs text-gray-500 mb-3">Change the password for either account. Passwords are stored using salted bcrypt hashing.</p>
            <form onSubmit={changePw} className="space-y-3">
              <Field label="Account">
                <select data-testid="pw-user-select" className="bdx-input" value={pwUser} onChange={(e) => setPwUser(e.target.value)}>
                  <option value="BDXGroup">BDXGroup — Administrator</option>
                  <option value="BLGGroup">BLGGroup — Read Only</option>
                </select>
              </Field>
              <Field label="New Password (min 12 characters)">
                <input data-testid="pw-input" type="password" className="bdx-input" minLength={12} required value={pw} onChange={(e) => setPw(e.target.value)} />
              </Field>
              <button data-testid="pw-save" className="bdx-btn-primary">Update Password</button>
            </form>
          </section>
        )}
      </div>

      <section className="mt-5 max-w-[1300px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="bdx-panel-header">
          <div className="flex items-center gap-3">
            <div className="bdx-icon-tile"><Recycle size={16} strokeWidth={1.9} /></div>
            <h2 className="font-heading text-base font-bold text-gray-950">Recycle Bin</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="bdx-table" data-testid="recycle-table">
          <thead><tr><th>Project No.</th><th>Type</th><th>Address</th><th>Deleted</th>{!readOnly && <th className="w-60">Actions</th>}</tr></thead>
          <tbody>
            {recycled.length === 0 && <tr><td colSpan={5} className="text-center text-gray-400 py-8">Recycle bin is empty</td></tr>}
            {recycled.map((p) => (
              <tr key={p.id}>
                <td className="font-mono-data font-medium">{p.project_number}</td>
                <td>{p.project_type}</td>
                <td>{p.label}</td>
                <td className="font-mono-data text-gray-500">{fmtDateTime(p.deleted_at)}</td>
                {!readOnly && (
                  <td>
                    <div className="flex gap-2">
                      <button data-testid={`restore-${p.project_number}`} className="bdx-btn-secondary text-xs" onClick={() => restore(p)}><RotateCcw size={13} strokeWidth={1.8} /> Restore</button>
                      <button data-testid={`perm-delete-${p.project_number}`} className="bdx-btn-danger text-xs" onClick={() => { setConfirmTarget(p); setConfirmText(""); }}><Trash2 size={13} strokeWidth={1.8} /> Delete Permanently</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>

      {confirmTarget && (
        <Modal title="Permanently delete project?" onClose={() => setConfirmTarget(null)}>
          <p className="text-sm text-gray-600">
            This will permanently destroy all data for <strong>{confirmTarget.project_type} {confirmTarget.project_number} — {confirmTarget.label}</strong>,
            including dwellings, variations, claims, notes, document records and audit history. This cannot be undone.
          </p>
          <Field label={`Type the project number (${confirmTarget.project_number}) to confirm`} className="mt-4">
            <input data-testid="perm-delete-confirm-input" className="bdx-input font-mono-data" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2 mt-5">
            <button data-testid="perm-delete-cancel" className="bdx-btn-secondary" onClick={() => setConfirmTarget(null)}>Cancel</button>
            <button data-testid="perm-delete-confirm-btn" className="bdx-btn-danger" disabled={confirmText !== confirmTarget.project_number} onClick={permanentDelete}>
              Permanently Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
